import * as React from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LoafCard from "../components/LoafCard";
import { Launch } from "@mui/icons-material";

// Function to get color-coded chips for tech stack
const getChipColor = (techName) => {
  const tech = techName.toLowerCase();
  if (
    tech.includes("react") ||
    tech.includes("material ui") ||
    tech.includes("next")
  ) return "#64b5f6";
  if (
    tech.includes("fastapi") ||
    tech.includes("python") ||
    tech.includes("ruby") ||
    tech.includes("node")
  ) return "#ff8a65";
  if (
    tech.includes("postgres") ||
    tech.includes("sqlite") ||
    tech.includes("mysql")
  ) return "#ba68c8";
  if (
    tech.includes("chromadb") ||
    tech.includes("pinecone") ||
    tech.includes("pgvector") ||
    tech.includes("embeddings") ||
    tech.includes("cli")
  ) return "#81c784";
  if (
    tech.includes("gemini") ||
    tech.includes("openai") ||
    tech.includes("anthropic") ||
    tech.includes("huggingface") ||
    tech.includes("genai") ||
    tech.includes("nlp")
  ) return "#4db6ac";
  if (
    tech.includes("blockchain") ||
    tech.includes("cryptography") ||
    tech.includes("crypto")
  ) return "#ffb74d";
  return "#f06292";
};

// Other tools data (move from Projects page)
const otherTools = [
  {
    key: "query",
    title: "query",
    url: "https://query.loafstack.dev",
    tech: ["React", "FastAPI", "Python", "ChromaDB", "Gemini AI"],
    bullets: [
      "Semantic document search engine using LangChain and ChromaDB",
      "Natural language Q&A across multi-format document uploads (PDF, DOCX, TXT, CSV)",
      "Google Gemini integration for intelligent, context-aware answers",
      "Advanced vector similarity search with metadata filtering",
      "Multi-document memory tracking with conversational follow-ups",
      "Role-based access and query history logging",
      "Real-time PDF parsing and text extraction pipeline",
    ],
  },
  {
    key: "notes",
    title: "notes",
    url: "https://notes.loafstack.dev",
    tech: ["React", "FastAPI", "Python", "Pinecone", "Embeddings", "GenAI"],
    bullets: [
      "AI-powered sop generation from unstructured meeting notes and transcripts",
      "FastAPI backend with Gemini embeddings and sop generation via structured prompts",
      "Semantic vector search using Pinecone with pgvector fallback support",
      "Real-time filtering, tag-based organization, and sop approval workflows",
      "Interface built with React + Material UI and optimized for PWA use",
      "Multi-format export (json, csv, txt) with version control and metadata tracking",
      "Dynamic filtering by topic and date",
    ],
  },
];

export default function Tools() {
  const navigate = useNavigate();
  const handleCardClick = (url) => {
    if (url.startsWith("/")) {
      navigate(url);
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };
  return (
    <Box sx={{ minHeight: "100vh", width: "100%", px: { xs: 1, sm: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
      <Box sx={{ width: "100%", mb: 5 }}>
        <Typography variant="h4" fontWeight={900} sx={{ mb: 2, color: "#fff", letterSpacing: 1 }}>Other Tools</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: { xs: 2, md: 3 } }}>
          {otherTools.map((it) => (
            <LoafCard key={it.key}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                <Typography variant="h6" fontWeight={800}>{it.title}</Typography>
                <IconButton
                  onClick={() => handleCardClick(it.url)}
                  size="small"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    '&:hover': { color: "#79d1c3", backgroundColor: "rgba(121, 209, 195, 0.1)" },
                  }}
                >
                  <Launch fontSize="small" />
                </IconButton>
              </Box>
              <ul style={{ margin: 0, paddingLeft: "1.2rem", lineHeight: 1.6, opacity: 0.9 }}>
                {it.bullets.map((b, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "12px" }}>
                    <span style={{ fontSize: "0.9rem" }}>{b}</span>
                  </li>
                ))}
              </ul>
              <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
                {it.tech.map((t) => {
                  const color = getChipColor(t);
                  return (
                    <Chip
                      key={t}
                      label={t}
                      size="small"
                      sx={{
                        backgroundColor: `${color}20`,
                        border: `1px solid ${color}60`,
                        color: color,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        '&:hover': { backgroundColor: `${color}30`, transform: "scale(1.02)" },
                      }}
                    />
                  );
                })}
              </Box>
            </LoafCard>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
